<template>
  <div class="outreach-contact-engagement">
    <BaseOutreachContactStat
      v-if="!hasAvgTotalEngagement"
      :tooltip="
        getLocaleText('outreach_contact_engagement.no_avg_engagement_tooltip')
      "
    >
      <div class="outreach-contact-engagement__no-value">
        {{ avgTotalEngagementFormatted }}
      </div>
    </BaseOutreachContactStat>

    <BaseButtonDropdown
      v-else
      interaction="hover"
      new-design
      new-dropdown
    >
      <template #btn="{ listeners }">
        <BaseOutreachContactStat v-on="listeners">
          {{ avgTotalEngagementFormatted }}
        </BaseOutreachContactStat>
      </template>
      <div class="outreach-contact-engagement__stats-container">
        <div class="outreach-contact-engagement__stats-title">
          {{ getLocaleText("outreach_contact_engagement.avg_engagement") }}
        </div>

        <div class="outreach-contact-engagement__stats-row">
          <div
            v-for="stat in stats"
            :key="stat.id"
            class="outreach-contact-engagement__stat"
          >
            <QIcon
              class="outreach-contact-engagement__stat-icon"
              :name="stat.icon"
            />

            <div
              class="outreach-contact-engagement__stat-value"
              :class="`outreach-contact-engagement__stat-${stat.id}`"
            >
              {{ stat.value }}
            </div>
          </div>
        </div>
      </div>
    </BaseButtonDropdown>
  </div>
</template>

<script setup>
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButtonDropdown } from "shared/components/base";
import BaseOutreachContactStat from "shared/components/Outreach/OutreachContact/BaseOutreachContactStat.vue";
import { SOCIAL_ICONS } from "shared/constants";
import { prettyNumber } from "shared/helpers/number";

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

function formatNumber(number) {
  return prettyNumber(Number(number).toFixed(), {
    notation: "compact",
  });
}

const avgTotalEngagement = computed(
  () => props.contact.averageTotalEngagements
);

const hasAvgTotalEngagement = computed(() => Boolean(avgTotalEngagement.value));

const avgTotalEngagementFormatted = computed(() =>
  String(formatNumber(avgTotalEngagement.value))
);

const stats = computed(() => [
  {
    id: "facebook",
    icon: SOCIAL_ICONS.Facebook,
    value: formatNumber(props.contact.averageFacebookEngagement),
  },
  {
    id: "twitter",
    icon: SOCIAL_ICONS.Twitter,
    value: formatNumber(props.contact.averageTwitterShares),
  },
  {
    id: "reddit",
    icon: SOCIAL_ICONS.Reddit,
    value: formatNumber(props.contact.averageRedditEngagements),
  },
  {
    id: "pinterest",
    icon: SOCIAL_ICONS.Pinterest,
    value: formatNumber(props.contact.averagePinterestShares),
  },
]);
</script>

<style lang="scss" scoped>
.outreach-contact-engagement {
  font-weight: 500;
  line-height: 150%;

  &__no-value {
    color: var(--s-color-denim-5);
    text-decoration: none;
  }

  &__stats {
    &-container {
      display: flex;
      flex-direction: column;
      gap: var(--s-spacing-lg);
      padding: var(--s-spacing-lg);
    }

    &-title {
      font-weight: var(--s-font-weight-bold);
      line-height: 150%;
      color: var(--s-color-denim-9);
    }

    &-row {
      display: flex;
      flex: 1;
      justify-content: space-between;
      gap: 24px;
    }
  }

  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;

    &-icon {
      font-size: 24px;
    }

    &-value {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      background-color: var(--s-color-denim-6);
      color: $white;
      border-radius: 50%;
      font-weight: 500;
    }

    &-facebook {
      background-color: #3662e3;
    }

    &-twitter {
      background-color: #000;
    }

    &-reddit {
      background-color: #d9622b;
    }

    &-pinterest {
      background-color: #ca3a31;
    }
  }
}
</style>
